import RcInstanceDTO from '@robocorp/robocloud-internal-cds-sdk/lib/sdk/Types';
import { IdServiceDTO } from '@robocorp/id-sdk';
import { dashboardApi, dashboardUserApi, idServiceUserApi } from '~/utils/api';

const mapIdServiceUserToRcUser = (user: IdServiceDTO.User): Partial<RcInstanceDTO.User> => {
  const details: RcInstanceDTO.UserDetails = {
    email: user.email,
    email_verified: user.emailVerified,
    first_name: user.profile.firstName,
    last_name: user.profile.lastName,
    country: user.profile.country,
    company_name: user.profile.companyName,
    job_role: user.profile.jobRole,
    language: user.profile.language,
    nickname: user.profile.displayName,
    onboarding_ts: user.appMeta.onboardingTs,
    picture: user.profile.pictureUrl,
    pp_accepted_ts: user.activeTs,
    skip_example_org: user.appMeta.skipExampleOrg,
    slack_invite_dismissed_ts: user.appMeta.slackInviteDismissTs,
    slack_invite_requested: user.appMeta.slackInvite,
    theme: user.profile.theme,
    tos_accepted_ts: user.appMeta.touAcceptTs,
    cloud_product_onboarding_ts: user.appMeta.cloudProductOnboardingTs,
    instructions_step: user.appMeta.instructionsStep,
  };

  return {
    id: user.id,
    create_ts: user.createTs,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    state: user.state as any,
    root: false,
    invited_by: undefined,
    details,
  };
};

const mapRcUserDetailsToIdServiceUser = (
  details: RcInstanceDTO.UserDetails,
): Partial<IdServiceDTO.User> => {
  return {
    id: undefined,
    email: details.email,
    emailVerified: details.email_verified,
    profile: {
      displayName: details.nickname,
      firstName: details.first_name,
      lastName: details.last_name,
      country: details.country,
      companyName: details.company_name,
      pictureUrl: details.picture,
      language: details.language,
      theme: details.theme,
    },
    extId: undefined,
    activeTs: undefined,
    createTs: undefined,
    state: undefined,
    appMeta: {
      slackInvite: details.slack_invite_requested,
      slackInviteDismissTs: details.slack_invite_dismissed_ts,
      instructionsStep: details.instructions_step,
    },
  };
};

export const getCurrentUser = async (login?: boolean) =>
  dashboardUserApi.get<RcInstanceDTO.User>('/me', { login });

export const deleteCurrentUser = async () => dashboardUserApi.delete<number>('/me');

export const editCurrentUserDetails = async (userDetails: RcInstanceDTO.UserDetails) =>
  idServiceUserApi.put<RcInstanceDTO.User>('/me', userDetails, {
    transformRequest: (data) => {
      const result = mapRcUserDetailsToIdServiceUser(data);
      delete result.email;
      delete result.emailVerified;
      return JSON.stringify(result);
    },
    transformResponse: (rawData) => {
      try {
        const data = JSON.parse(rawData);
        return mapIdServiceUserToRcUser(data);
      } catch (error) {
        return rawData;
      }
    },
  });

export const acceptAgreements = async () => dashboardUserApi.post<void>('/me/tos');

export const updateUserDetails = async (partialUserDetails: Partial<RcInstanceDTO.UserDetails>) =>
  dashboardUserApi.patch<Partial<RcInstanceDTO.UserDetails>>('/me', partialUserDetails);

export const postUserLogout = async () => dashboardUserApi.post<RcInstanceDTO.User>('/me/logout');

export const updateUserMFAConfiguration = async ({
  mfaEnrollment,
}: {
  mfaEnrollment: 'request_enrollment' | 'request_unenrollment';
}) => dashboardApi.post<{ success: true }>('/user-mfa', { mfaEnrollment });
